import React, { useEffect, useRef } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { ArrowRightIcon, PlayCircleIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export function AnimatedCounter({
  value,
  suffix,
  inView
}: {
  value: number;
  suffix: string;
  inView: boolean;
}) {
  const count = useMotionValue(0);
  const rounded = useTransform(count, latest => Math.round(latest).toString());
  useEffect(() => {
    if (inView) {
      const controls = animate(count, value, {
        duration: 2,
        ease: [0.16, 1, 0.3, 1]
      });
      return controls.stop;
    }
  }, [inView, value]);
  return <span className="flex items-baseline" data-id="element-198">
      <motion.span data-id="element-199">{rounded}</motion.span>
      <span data-id="element-200">{suffix}</span>
    </span>;
}
export function Hero() {
  const {
    t
  } = useLanguage();
  const statsRef = useRef(null);
  const isStatsInView = useInView(statsRef, {
    once: true,
    margin: '-30px'
  });
  const stats = [{
    value: 50,
    suffix: '+',
    label: t('hero.stat.companies'),
    emoji: '🏢'
  }, {
    value: 12,
    suffix: '',
    label: t('hero.stat.countries'),
    emoji: '🌍'
  }, {
    value: 30,
    suffix: '+',
    label: t('hero.stat.projects'),
    emoji: '🚀'
  }, {
    value: 100,
    suffix: '+',
    label: t('hero.stat.events'),
    emoji: '🎯'
  }];
  return <section className="relative w-full overflow-hidden px-4 pt-28 pb-6 md:pt-32 md:pb-8 flex flex-col" style={{
    backgroundColor: 'var(--bg-primary)',
    minHeight: '100vh'
  }} data-id="element-201">
      {/* Subtle background accent */}
      <div className="absolute inset-0 overflow-hidden opacity-20 pointer-events-none" data-id="element-202">
        <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)'
      }} data-id="element-203" />
      </div>

      <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col md:flex-row items-center justify-between gap-10 flex-1" data-id="element-204">
        {/* Left Side: Text Content (55%) */}
        <div className="w-full md:w-[55%] flex flex-col items-start text-left" data-id="element-205">
          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.1,
          ease: [0.22, 1, 0.36, 1]
        }} className="mb-5 text-4xl md:text-5xl lg:text-7xl font-bold tracking-tight" style={{
          color: 'var(--text-primary)'
        }} data-id="element-206">
            {t('hero.title')}
            <br data-id="element-207" />
            <span style={{
            color: 'var(--text-primary)'
          }} data-id="element-208">
              {t('hero.subtitle')}
            </span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.25,
          ease: [0.22, 1, 0.36, 1]
        }} className="mb-8 max-w-xl text-base md:text-xl" style={{
          color: 'var(--text-secondary)',
          lineHeight: '1.7'
        }} data-id="element-209">
            {t('hero.desc')}
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.35,
          ease: [0.22, 1, 0.36, 1]
        }} className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto" data-id="element-210">
            <a href="#about" className="group flex items-center justify-center gap-2 rounded-xl px-8 py-4 font-medium transition-all border hover:scale-105" style={{
            borderColor: 'var(--border)',
            backgroundColor: 'var(--bg-secondary)',
            color: 'var(--text-primary)'
          }} data-id="element-211">
              {t('hero.cta.more')}
            </a>
            <a href="#join" className="group flex items-center justify-center gap-2 rounded-xl px-8 py-4 font-medium transition-all hover:scale-105" style={{
            backgroundColor: 'var(--accent)',
            color: '#FFFFFF'
          }} data-id="element-212">
              {t('hero.cta.join')}
              <ArrowRightIcon className="h-5 w-5 transition-transform group-hover:translate-x-1" data-id="element-213" />
            </a>
          </motion.div>
        </div>

        {/* Right Side: Placeholder (45%) */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.7,
        delay: 0.2,
        ease: [0.22, 1, 0.36, 1]
      }} className="w-full md:w-[45%] aspect-video md:aspect-square lg:aspect-[4/3] rounded-3xl border-2 border-dashed flex flex-col items-center justify-center gap-4 transition-colors hover:bg-[var(--bg-tertiary)]" style={{
        borderColor: 'var(--border)',
        backgroundColor: 'var(--bg-secondary)'
      }} data-id="element-214">
          <PlayCircleIcon className="w-16 h-16 opacity-50" style={{
          color: 'var(--text-muted)'
        }} data-id="element-215" />
          <span className="text-lg font-medium opacity-50" style={{
          color: 'var(--text-muted)'
        }} data-id="element-216">
            {t('hero.placeholder')}
          </span>
        </motion.div>
      </div>

      {/* Stats — no separator, tighter spacing, part of hero viewport */}
      <div ref={statsRef} className="relative z-10 mx-auto max-w-7xl w-full pt-8 pb-2" data-id="element-217">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8" data-id="element-218">
          {stats.map((stat, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} animate={isStatsInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.5 + index * 0.12,
          ease: [0.22, 1, 0.36, 1]
        }} className="flex items-center gap-4" data-id="element-219">
              <div className="w-14 h-14 rounded-full flex items-center justify-center text-3xl flex-shrink-0" style={{
            backgroundColor: 'var(--bg-tertiary)'
          }} data-id="element-220">
                {stat.emoji}
              </div>
              <div className="flex flex-col" data-id="element-221">
                <div className="text-2xl sm:text-3xl font-bold" style={{
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em'
            }} data-id="element-222">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} inView={isStatsInView} data-id="element-223" />
                </div>
                <div className="text-xs sm:text-sm uppercase tracking-wider font-medium" style={{
              color: 'var(--text-muted)'
            }} data-id="element-224">
                  {stat.label}
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}